import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { updateMonitoringInfo } from "../UpdateMonitoringInfo";
import { CAProvinces, countryList, stateList } from "../../../../util/CommonLists";
import { useDispatch, useSelector } from "react-redux";
import { getFlowByName } from "../../../../config/FlowName";
import * as NextStepFlowAction from "../../../../store/nextStepFlowService/actions";
import { ApplicationState } from "../../../../store";
import { GetCountryListAndInternationalSupport } from "../../../../util/countryListAndInternationalSupport";
import i18nService from "../../../../util/Internationalization/i18n/i18n";
import { PATTERNS } from "../../../../util/patterns";
import { GetAccountInformation } from "../../../../store/service";
import { useTranslation } from "react-i18next";

interface IProps {
    refreshDataCallback(): void;
    setFormStatus: any;
    formData: any;
    personalInfo: any;
    IsIDFUser: any;
}

const MonitoringAddressInfoForm = ({ refreshDataCallback, setFormStatus, formData, personalInfo, IsIDFUser }: IProps) => {
    const [loading, setLoading] = useState(false);
    const { t: tLang, i18n: i18nLang } = useTranslation();
    const { register, handleSubmit, errors, setValue, watch } = useForm({
        mode: "onBlur",
    });
    const [stateVal, setStateVal] = useState('')

    useEffect(()=>{
        setStateVal(watch('state'));
    }, watch('state'))
    const dispatch = useDispatch();
    const { supportInternational } = GetCountryListAndInternationalSupport();
    const monitoringInfoFlow = useSelector((state: ApplicationState) => getFlowByName(state, "ADD-YOUR-ADDITIONAL-INFORMATION"));
    const businessdMonitoringInfoFlow = useSelector((state: ApplicationState) => getFlowByName(state, "ADD-YOUR-ADDITIONAL-BUSINESS-INFORMATION"));
    const completeGetStartedItem = (flowID: any, flowName: string) => dispatch(NextStepFlowAction.actionCreators.completeGetStartedItem(flowID, flowName));
    const [country, setCountry] = useState("");
    const isPrimaryAccount = useSelector((state: ApplicationState) =>
        state.loggedInUser && state.loggedInUser.activeAccount && state.loggedInUser.activeAccount.accountType
            ? state.loggedInUser.activeAccount.accountType.toLowerCase() === "primary"
            : false
    );

    useEffect(() => {
        setLoading(true);
        GetAccountInformation().then((data: any) => {
            const countryCode: any = !!formData.country
                ? formData.country.length > 2
                    ? countryList.find((country) => country.name === formData.country)
                    : { code: formData.country }
                : { code: "US" };
            const accountCountryCode: any =
                formData.country.length > 2
                    ? countryList.find((country) => {
                          if (
                              data["_unifiedAccountInfo"].country.length > 2
                                  ? country.name === data["_unifiedAccountInfo"].country
                                  : country.code === data["_unifiedAccountInfo"].country
                          ) {
                              setLoading(false);
                              return country.code;
                          }
                      })
                    : { code: data["_unifiedAccountInfo"].country };

            if (accountCountryCode.code !== countryCode.code && data["_unifiedAccountInfo"].country !== "") {
                setValue("city", "");
                setValue("zipcode", "");
                if (i18nService.get("Country") !== "US" && i18nService.get("Country") !== "CA") {
                    setValue("state", "");
                } else {
                    formData.state = "";
                }
            }
            if (!!data["_unifiedAccountInfo"].country) {
                setCountry(data["_unifiedAccountInfo"].country);
                i18nService.setRegionBasedonCountry(data["_unifiedAccountInfo"].country);
            } else {
                if(!supportInternational){
                    setCountry("US");
                    i18nService.setRegionBasedonCountry("US");
                }
              
            }
            setLoading(false);
        });
    }, []);

    const onSubmit = (data: any) => {
        if (country !== "US" && country !== "CA") {
            data.state = data.state.toUpperCase().substring(0, 50);
        }
        setLoading(true);
        data.country = country;
        updateMonitoringInfo(personalInfo, data, "", formData, isPrimaryAccount).then((data: any) => {
            if (monitoringInfoFlow && monitoringInfoFlow.flowStepStatus !== "COMPLETED") {
                completeGetStartedItem(monitoringInfoFlow.flowStepId, monitoringInfoFlow.flowName ? monitoringInfoFlow.flowName : " ");
            }
            if (businessdMonitoringInfoFlow && businessdMonitoringInfoFlow.flowStepStatus !== "COMPLETED") {
                completeGetStartedItem(businessdMonitoringInfoFlow.flowStepId, businessdMonitoringInfoFlow.flowName ? businessdMonitoringInfoFlow.flowName : " ");
            }
            refreshDataCallback();
            setLoading(false);

            setFormStatus(false);
        });
    };

    return (
        <div id="monitored-info-address-form" className={`${loading && "loading"}`}>
            <form onSubmit={handleSubmit(onSubmit)} id="monitored-info-address-form-container">
                <fieldset className="form-row no-margin inline">
                    <div className="row">
                        <div className="col-xs-12 col-sm-4 col-no-gutter">
                            <legend className="legend-label"> <label htmlFor="label-address-line-1">
                                {tLang("identityVault.monitoredInformation.addressWidget.label.street")}</label>
                            <span className="required" title="Mandatory">*</span>
                            </legend>
                        </div>

                        <div className="col-xs-12 col-sm-4 col-no-gutter">
                            <div className={`form-row row ${errors["address1"] && "status-error"}`}>
                                <div className="form-label sr-only col-xs-12 col-no-gutter">
                                    <label htmlFor="label-address-line1">
                                        Address Line 1{" "}
                                        <span className="required" title="Mandatory">
                                            *
                                        </span>
                                    </label>
                                </div>
                                <div role="alert" className="form-input col-xs-12 col-no-gutter">
                                    <input
                                        aria-required="true"
                                        type="text"
                                        defaultValue={formData.address1}
                                        name="address1"
                                        className="inputbox"
                                        id="label-address-line-1"
                                        required
                                        ref={register({ required: true, pattern: PATTERNS.STREETADDRESS.pattern })}
                                        placeholder={tLang("identityVault.monitoredInformation.addressWidget.placeholder.addressLine1")}
                                    />
                                    {errors["address1"] && errors["address1"].type === "required" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.addressWidget.validationMessage.address1Required")}
                                        </span>
                                    )}
                                    {errors["address1"] && errors["address1"].type === "pattern" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.addressWidget.validationMessage.address1Pattern")}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={`form-row row ${errors["address2"] && "status-error"}`}>
                                <div className="form-label sr-only col-xs-12 col-no-gutter">
                                    <label htmlFor="label-address-line-2">Address Line 2 </label>
                                </div>
                                <div role="alert" className="form-input col-xs-12 col-no-gutter">
                                    <input
                                        aria-required="true"
                                        type="text"
                                        defaultValue={formData.address2.includes(';') ?  formData.address2.split(';')[0] : formData.address2}
                                        name="address2"
                                        className="inputbox"
                                        id="label-address-line-2"
                                        ref={register({ pattern: PATTERNS.STREETADDRESS.pattern })}
                                        placeholder={tLang("identityVault.monitoredInformation.addressWidget.placeholder.addressLine2")}
                                    />
                                    {errors["address2"] && errors["address2"].type === "pattern" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.addressWidget.validationMessage.address2Pattern")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
                {(stateVal==="PR") && <div className={`form-row inline row ${errors["urbanization"] && "status-error"}`}>
                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                        <label htmlFor="label-address-urbanization">
                            Urbanization
                        </label>
                    </div>
                    <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                        <input
                            type="text"
                            name="urbanization"
                            defaultValue={formData.address2.includes(';') ?  formData.address2.split(';')[1] : ''}
                            className="inputbox"
                            id="label-address-urbanization"
                            ref={register({ required: false })}
                            maxLength={35}
                            placeholder='Urbanization(Optional)'
                        />
                    </div>
                </div>}
                <div className={`form-row inline row ${errors["city"] && "status-error"}`}>
                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                        <label htmlFor="label-address-city">
                            {tLang("identityVault.monitoredInformation.addressWidget.label.city")}{" "}
                            <span className="required" title="Mandatory">
                                *
                            </span>
                        </label>
                    </div>
                    <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                        <input
                            aria-required="true"
                            type="text"
                            name="city"
                            defaultValue={formData.city}
                            className="inputbox"
                            id="label-address-city"
                            ref={register({ required: true, pattern: PATTERNS.CITY.pattern })}
                            required
                            placeholder={tLang("identityVault.monitoredInformation.addressWidget.placeholder.city")}
                        />
                        {errors["city"] && errors["city"].type === "required" && (
                            <span className="form-error">{tLang("identityVault.monitoredInformation.addressWidget.validationMessage.cityRequired")}</span>
                        )}
                        {errors["city"] && errors["city"].type === "pattern" && (
                            <span className="form-error">{tLang("identityVault.monitoredInformation.addressWidget.validationMessage.cityPattern")}</span>
                        )}
                    </div>
                </div>

                <div className={errors["state"] ? "form-row inline row status-error" : "form-row inline row"}>
                    {country === "US" || !supportInternational ? (
                        <div className="form-row inline row">
                            <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                <label htmlFor="label-address-state">
                                    {tLang("identityVault.monitoredInformation.addressWidget.label.state")}{" "}
                                    <span className="required" title="Mandatory">
                                        *
                                    </span>
                                </label>
                            </div>
                            <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                <select
                                    aria-required="true"
                                    className="inputbox"
                                    id="label-address-state"
                                    defaultValue={formData.state}
                                    name="state"
                                    ref={register({ required: true, validate: (value) => value !== "---" })}
                                >
                                    {stateList.map((val: any) => (
                                        <option key={val.name} value={val.value}>
                                            {val.name}
                                        </option>
                                    ))}
                                </select>
                                {errors["state"] && errors["state"].type === "validate" && (
                                    <span className="form-error">{tLang("identityVault.monitoredInformation.addressWidget.validationMessage.state")}</span>
                                )}
                            </div>
                        </div>
                    ) : country === "CA" ? (
                        <div className="form-row inline row">
                            <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                <label htmlFor="label-address-state">
                                        {tLang("identityVault.monitoredInformation.addressWidget.label.province")}{" "}
                                    <span className="required" title="Mandatory">
                                        *
                                    </span>
                                </label>
                            </div>
                            <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                <select
                                    aria-required="true"
                                    className="inputbox"
                                    id="label-address-state"
                                    defaultValue={formData.state}
                                    name="state"
                                    ref={register({ required: true, validate: (value) => value !== "---" })}
                                >
                                    {CAProvinces.map((val: any) => (
                                        <option key={val.name} value={val.value}>
                                            {val.name}
                                        </option>
                                    ))}
                                </select>
                                    {errors["state"] && errors["state"].type === "validate" && <span className="form-error">{tLang("identityVault.monitoredInformation.addressWidget.validationMessage.state")}</span>}
                            </div>
                        </div>
                    ) : (
                        <div className="form-row inline row">
                            <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                <label htmlFor="label-address-state">
                                        {tLang("identityVault.monitoredInformation.addressWidget.label.region")}{" "}
                                    <span className="required" title="Mandatory">
                                        *
                                    </span>
                                </label>
                            </div>
                            <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                <input
                                    aria-required="true"
                                    type="text"
                                    name="state"
                                    defaultValue={formData.state}
                                    id="label-address-state"
                                    ref={register({ required: true, pattern: PATTERNS.REGION.pattern })}
                                    className="inputbox"
                                    placeholder="Region"
                                />
                                {errors["state"] && errors["state"].type === "required" && (
                                    <span className="form-error">{tLang("identityVault.monitoredInformation.addressWidget.validationMessage.regionRequired")}</span>
                                )}
                                {errors["state"] && errors["state"].type === "pattern" && (
                                    <span className="form-error">{tLang("identityVault.monitoredInformation.addressWidget.validationMessage.regionPattern")}</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className={`form-row inline row ${errors["zipcode"] && "status-error"}`}>
                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                        <label htmlFor="label-address-zipcode">
                            {i18nService.get("ZipCode.Label") === "Zip Code"
                                ? tLang("identityVault.monitoredInformation.addressWidget.label.zipcode")
                                : i18nService.get("ZipCode.Label") === "Postal Code"
                                    ? tLang("identityVault.monitoredInformation.addressWidget.label.postalCode")
                                    : i18nService.get("ZipCode.Label")} {" "}
                            <span className="required" title="Mandatory">
                                *
                            </span>
                        </label>
                    </div>
                    <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                        <input
                            aria-required="true"
                            type="text"
                            className="inputbox"
                            id="label-address-zipcode"
                            required
                            defaultValue={formData.zipcode}
                            name="zipcode"
                            maxLength={i18nService.get("ZipCode.MaxLength")}
                            ref={register({
                                required: true,
                                pattern: PATTERNS[i18nService.get("ZipCode.Pattern")].pattern,
                                maxLength: i18nService.get("ZipCode.MaxLength"),
                            })}
                            placeholder={
                                i18nService.get("ZipCode.Label") === "Zip Code"
                                    ? tLang("identityVault.monitoredInformation.addressWidget.label.zipcode")
                                    : i18nService.get("ZipCode.Label") === "Postal Code"
                                        ? tLang("identityVault.monitoredInformation.addressWidget.label.postalCode")
                                        : i18nService.get("ZipCode.Label")
                            }
                        />
                        {errors["zipcode"] && errors["zipcode"].type === "required" && (
                            <span className="form-error">
                                {i18nService.get("ZipCode.Label") === "Zip Code"
                                    ? tLang("identityVault.monitoredInformation.addressWidget.validationMessage.zipcodeRequired")
                                    : i18nService.get("ZipCode.Label") === "Postal Code"
                                        ? tLang("identityVault.monitoredInformation.addressWidget.validationMessage.postalcodeRequired")
                                        : `${i18nService.get("ZipCode.Label")} is required.`}
                            </span>
                        )}
                        {errors["zipcode"] && errors["zipcode"].type === "pattern" && (
                            <span className="form-error">
                                {i18nService.get("ZipCode.Pattern") === "ZIPCODE"
                                    ? `${tLang("identityVault.monitoredInformation.addressWidget.validationMessage.formatInvalidMessage")} ${tLang(
                                          "identityVault.monitoredInformation.addressWidget.validationMessage.zipcodePattern"
                                      )}`
                                    : `${tLang("identityVault.monitoredInformation.addressWidget.validationMessage.formatInvalidMessage")}
                                       ${i18nService.get("ZipCode.Label") === "Postal Code" ? tLang("identityVault.monitoredInformation.addressWidget.label.postalCode") : i18nService.get("ZipCode.Label") }
                                       ${tLang("identityVault.monitoredInformation.addressWidget.validationMessage.postalCodeFormatMessage")}`}
                            </span>
                        )}
                        {errors["zipcode"] && errors["zipcode"].type === "maxLength" && (
                            <span className="form-error">
                                {i18nService.get("ZipCode.Label") === "Zip Code"
                                    ? `${tLang("identityVault.monitoredInformation.addressWidget.validationMessage.formatInvalidMessage")} ${tLang(
                                          "identityVault.monitoredInformation.addressWidget.validationMessage.zipcodePattern"
                                      )}`
                                    : `${tLang("identityVault.monitoredInformation.addressWidget.validationMessage.formatInvalidMessage")}
                                        ${i18nService.get("ZipCode.Label") === "Postal Code" ? tLang("identityVault.monitoredInformation.addressWidget.label.postalCode") : i18nService.get("ZipCode.Label") }
                                       
                                      ${tLang("identityVault.monitoredInformation.addressWidget.validationMessage.postalCodeLengthMessage", { ZipCodeLength: i18nService.get("ZipCode.MaxLength") })}`}
                            </span>
                        )}
                    </div>
                </div>

                <div className="margin-vertical-l"></div>

                <div className="row between-xs">
                    <div className="col-xs col-no-gutter flex-grow-null"></div>
                    <div className="col-xs col-no-gutter flex-grow-null">
                        <ul className="list-inline list-reset">
                            <li>
                                <button
                                    type="button"
                                    className="btn-default btn-link btn-no-padding btn-no-border btn-min-width"
                                    onClick={() => {
                                        setFormStatus(false);
                                        window.addToDataLayer({ event: "Cancel Editing Address" });
                                    }}
                                >
                                    <span className="btn-text">{tLang("identityVault.monitoredInformation.addressWidget.cancelButtonText")}</span>
                                </button>
                            </li>
                            <li className="margin-left-s">
                                <button
                                    disabled={!(Object.keys(errors).length === 0)}
                                    type="submit"
                                    className="btn-default btn-primary btn-min-width"
                                    onClick={() => window.addToDataLayer({ event: "Save Edited Address" })}
                                >
                                    <span className="btn-text">{tLang("identityVault.monitoredInformation.addressWidget.saveButtonText")}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default MonitoringAddressInfoForm;
